import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search as SearchIcon, Star, Send, MapPin, BarChart3, Heart, Share2, BadgeCheck, SlidersHorizontal, Mic, Guitar, Drum, Piano, Music, Music2, CalendarClock, Check, Users } from "lucide-react";
import { api, apiError, LEVELS, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/components/PlanGate";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHIPS = [
  { v: "", label: "Todos", Icon: Music },
  { v: "Voz", label: "Vocal", Icon: Mic },
  { v: "Guitarra", label: "Guitarra", Icon: Guitar },
  { v: "Baixo", label: "Baixo", Icon: Guitar },
  { v: "Bateria", label: "Bateria", Icon: Drum },
  { v: "Teclado", label: "Teclado", Icon: Piano },
  { v: "Violão", label: "Violão", Icon: Guitar },
  { v: "Percussão", label: "Percussão", Icon: Drum },
  { v: "Saxofone", label: "Sax", Icon: Music2 },
  { v: "DJ", label: "DJ", Icon: Music2 },
];
const FAV_KEY = "mc_fav_musicians";
const lvlRank = (l) => LEVELS.indexOf(l);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Busca de músicos reutilizável — convida direto para o evento da sessão via onInvite(user).
export default function MusicianFinder({ onInvite, invitedIds = [], vacancyInstruments = [], eventInstruments = [], onNewInstrument, focusInstrument = "" }) {
  const { hasFeature } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ instrument: focusInstrument || vacancyInstruments[0] || "", q: "", specialty: "", min_stars: "", min_gigs: "", max_distance: "", date: "", start_time: "", end_time: "", only_available: false, verified_only: false });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uiLevel, setUiLevel] = useState("any");
  const [uiCity, setUiCity] = useState("any");
  const [sortBy, setSortBy] = useState("relevance");
  const [showFilters, setShowFilters] = useState(false);
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; } });
  const qTimer = useRef(null);
  const invitedSet = useMemo(() => new Set(invitedIds), [invitedIds]);

  const run = useCallback(async (override = {}) => {
    setLoading(true);
    const merged = { ...f, ...override };
    const params = {};
    Object.entries(merged).forEach(([k, v]) => { if (v !== "" && v != null && v !== false) params[k] = v; });
    try { const { data } = await api.get("/musicians/search", { params }); setResults(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => {
    if (!hasFeature("contractor_basic")) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serverKey = `${f.instrument}|${f.min_stars}`;
  const firstRender = useRef(true);
  useEffect(() => {
    if (!hasFeature("contractor_basic")) return;
    if (firstRender.current) { firstRender.current = false; return; }
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const onQ = (e) => {
    const q = e.target.value;
    setF((p) => ({ ...p, q }));
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => run({ q }), 450);
  };
  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };
  const shareProfile = async (u) => {
    const url = `${window.location.origin}/musicos/${u.id}`;
    const data = { title: `${u.stage_name} — Mí Chamou`, text: `Confira ${u.stage_name} no Mí Chamou`, url };
    try { if (navigator.share) { await navigator.share(data); return; } } catch { return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link do perfil copiado!"); } catch { toast.error("Não foi possível compartilhar."); }
  };

  const musicianLevel = useCallback((u) => {
    const lv = u.instrument_levels || {};
    if (f.instrument && lv[f.instrument]) return lv[f.instrument];
    const vals = Object.values(lv);
    if (!vals.length) return "";
    return vals.reduce((a, b) => (lvlRank(b) > lvlRank(a) ? b : a), vals[0]);
  }, [f.instrument]);
  const rateOf = useCallback((u) => {
    const rt = u.instrument_rates || {};
    if (f.instrument && rt[f.instrument] > 0) return rt[f.instrument];
    const vals = Object.values(rt).filter((v) => v > 0);
    return vals.length ? Math.min(...vals) : null;
  }, [f.instrument]);

  const cityOptions = useMemo(() => [...new Set(results.map((u) => [u.city, u.state].filter(Boolean).join(" - ")).filter(Boolean))], [results]);

  const view = useMemo(() => {
    let list = [...results];
    if (uiLevel !== "any") list = list.filter((u) => lvlRank(musicianLevel(u)) >= lvlRank(uiLevel));
    if (uiCity !== "any") list = list.filter((u) => [u.city, u.state].filter(Boolean).join(" - ") === uiCity);
    if (sortBy === "rating") list.sort((a, b) => (b.rating?.avg || 0) - (a.rating?.avg || 0));
    else if (sortBy === "distance") list.sort((a, b) => (a.distance_km ?? 9999) - (b.distance_km ?? 9999));
    else if (sortBy === "gigs") list.sort((a, b) => (b.gigs_done || 0) - (a.gigs_done || 0));
    return list;
  }, [results, uiLevel, uiCity, sortBy, musicianLevel]);

  const clearFilters = () => {
    setF({ instrument: "", q: "", specialty: "", min_stars: "", min_gigs: "", max_distance: "", date: "", start_time: "", end_time: "", only_available: false, verified_only: false });
    setUiLevel("any"); setUiCity("any"); setSortBy("relevance");
    setTimeout(() => run({ instrument: "", q: "", min_stars: "", max_distance: "", date: "", start_time: "", end_time: "", only_available: false, verified_only: false }), 0);
  };
  const hasActiveFilters = f.instrument || f.q || f.min_stars || f.max_distance || f.only_available || f.verified_only || uiLevel !== "any" || uiCity !== "any" || sortBy !== "relevance";
  const inputCls = "bg-[#09090B] border-white/10 rounded-xl";

  if (!hasFeature("contractor_basic")) return <PlanGate feature="contractor_basic" title="Buscar e convidar profissionais" />;

  return (
    <div className="space-y-5" data-testid="musician-finder">
      <div>
        <h2 className="font-head text-2xl font-semibold tracking-tight flex items-center gap-2"><SearchIcon className="h-6 w-6 text-accent" />Buscar músicos para convidar</h2>
        <p className="text-muted-foreground text-sm mt-1">Encontre talentos e envie o convite direto para este evento.</p>
      </div>

      <div className="relative">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input data-testid="mf-search-q" value={f.q} onChange={onQ} placeholder="Buscar por nome, instrumento ou estilo..." className="pl-12 h-13 py-3.5 bg-card border-white/10 rounded-2xl text-base" />
      </div>

      {vacancyInstruments.length > 0 && (
        <div className="rounded-2xl border border-accent/25 bg-accent/[0.05] p-3" data-testid="mf-vacancy-filters">
          <div className="text-[11px] uppercase tracking-widest text-accent font-bold mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Filtrar pelas vagas em aberto</div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="mf-vac-all" onClick={() => setF((p) => ({ ...p, instrument: "" }))}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${f.instrument === "" ? "border-accent bg-accent/15 text-accent" : "border-white/10 bg-card text-muted-foreground hover:border-white/25"}`}>Todas</button>
            {vacancyInstruments.map((ins) => (
              <button key={ins} data-testid={`mf-vac-${ins}`} onClick={() => setF((p) => ({ ...p, instrument: ins }))}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${f.instrument === ins ? "border-accent bg-accent/15 text-accent" : "border-white/10 bg-card text-muted-foreground hover:border-white/25"}`}>{ins}</button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="mf-instrument-chips">
        {CHIPS.map(({ v, label, Icon }) => {
          const active = f.instrument === v;
          return (
            <button key={v || "all"} data-testid={`mf-chip-${v || "all"}`} onClick={() => { setF((p) => ({ ...p, instrument: v })); if (v && onNewInstrument && !eventInstruments.includes(v)) onNewInstrument(v); }}
              className={`shrink-0 flex flex-col items-center justify-center gap-1 w-[74px] h-[74px] rounded-2xl border transition-all ${active ? "border-accent bg-accent/10 shadow-[0_0_15px_rgba(255,170,0,0.25)]" : "border-white/10 bg-card hover:border-white/20"}`}>
              <Icon className={`h-6 w-6 ${active ? "text-accent" : "text-muted-foreground"}`} />
              <span className={`text-xs font-medium ${active ? "text-accent" : "text-foreground"}`}>{label}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid="mf-filter-pills">
        <FilterSelect testid="mf-filter-rating" Icon={Star} label="Avaliação" value={f.min_stars || "0"} onChange={(v) => setF((p) => ({ ...p, min_stars: v === "0" ? "" : v }))}
          options={[["0", "Todos"], ["4", "4+ estrelas"], ["4.5", "4,5+ estrelas"]]} />
        <FilterSelect testid="mf-filter-level" Icon={BarChart3} label="Nível" value={uiLevel} onChange={setUiLevel}
          options={[["any", "Todos"], ...LEVELS.map((l) => [l, cap(l)])]} />
        <FilterSelect testid="mf-filter-city" Icon={MapPin} label="Cidade" value={uiCity} onChange={setUiCity}
          options={[["any", "Todas"], ...cityOptions.map((c) => [c, c])]} />
        <FilterSelect testid="mf-filter-sort" Icon={SlidersHorizontal} label="Ordenar por" value={sortBy} onChange={setSortBy}
          options={[["relevance", "Mais relevantes"], ["rating", "Melhor avaliação"], ["distance", "Mais perto"], ["gigs", "Mais eventos"]]} />
      </div>

      <button data-testid="mf-toggle-verified" onClick={() => { const v = !f.verified_only; setF((p) => ({ ...p, verified_only: v })); run({ verified_only: v }); }}
        className={`w-full flex items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-medium transition-all ${f.verified_only ? "border-accent bg-accent/10 text-accent" : "border-white/10 bg-card text-muted-foreground hover:border-white/20"}`}>
        <BadgeCheck className="h-4 w-4" />{f.verified_only ? "Mostrando só habilidade validada ✓" : "Somente habilidade validada pela curadoria"}
      </button>

      <button data-testid="mf-toggle-availability" onClick={() => setShowFilters((s) => !s)} className="w-full flex items-center justify-center gap-2 text-sm text-accent hover:underline">
        <CalendarClock className="h-4 w-4" />{showFilters ? "Ocultar disponibilidade" : "Filtrar por disponibilidade (data e hora)"}
      </button>
      {showFilters && (
        <div className="rounded-2xl border border-white/10 bg-card p-4 space-y-3" data-testid="mf-availability-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-muted-foreground text-xs">Data</Label><Input data-testid="mf-search-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div><Label className="text-muted-foreground text-xs">Início</Label><Input data-testid="mf-search-start" type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div><Label className="text-muted-foreground text-xs">Término</Label><Input data-testid="mf-search-end" type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-muted-foreground text-xs">Distância máx. (km)</Label><Input data-testid="mf-search-distance" type="number" min="0" value={f.max_distance} onChange={(e) => setF({ ...f, max_distance: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div className="flex items-end justify-between gap-2">
              <div><Label className="text-foreground text-sm">Somente disponíveis</Label><p className="text-[11px] text-muted-foreground">Remove conflitos reais de agenda.</p></div>
              <Switch data-testid="mf-only-available" checked={f.only_available} onCheckedChange={(v) => setF({ ...f, only_available: v })} />
            </div>
          </div>
          <Button data-testid="mf-apply-availability" onClick={() => run()} className="w-full bg-[#FF4D00] hover:bg-[#E64500] rounded-xl gap-2"><SearchIcon className="h-4 w-4" />Aplicar disponibilidade</Button>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground" data-testid="mf-results-count">{loading ? "Buscando…" : `${view.length} músico${view.length === 1 ? "" : "s"} encontrado${view.length === 1 ? "" : "s"}`}</span>
        {hasActiveFilters && <button data-testid="mf-clear-filters" onClick={clearFilters} className="text-sm text-[#FF4D00] hover:underline">Limpar filtros</button>}
      </div>

      {!loading && view.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-card p-12 text-center text-muted-foreground" data-testid="mf-no-results">Nenhum músico encontrado com esses filtros.</div>
      ) : (
        <div className="space-y-3">
          {view.map((u) => (
            <MusicianCard key={u.id} u={u} level={musicianLevel(u)} rate={rateOf(u)} fav={favs.includes(u.id)}
              invited={invitedSet.has(u.id)}
              skillVerified={f.instrument ? (u.verified_instruments || []).includes(f.instrument) : (u.verified_instruments || []).length > 0}
              onFav={() => toggleFav(u.id)} onShare={() => shareProfile(u)} onInvite={() => onInvite(u)} onProfile={() => navigate(`/musicos/${u.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

const FilterSelect = ({ testid, Icon, label, value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-auto py-2.5 bg-card border-white/10 rounded-2xl text-left [&>svg]:opacity-60">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-4 w-4 text-accent shrink-0" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">{label}</div>
          <SelectValue />
        </div>
      </div>
    </SelectTrigger>
    <SelectContent className="bg-[#121217] border-white/10">
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const MusicianCard = ({ u, level, rate, fav, invited, skillVerified, onFav, onShare, onInvite, onProfile }) => {
  const verified = (u.gigs_done || 0) > 0 || (u.rating?.count || 0) > 0;
  const cityStr = [u.city, u.state].filter(Boolean).join(" - ");
  const primaryInstr = u.instruments?.[0] || "Músico";
  return (
    <div data-testid={`mf-result-${u.id}`} className="flex gap-3 rounded-2xl border border-white/10 bg-card p-3 transition-all hover:border-accent/40">
      <button onClick={onProfile} aria-label={`Ver perfil de ${u.stage_name}`} className="h-[104px] w-[104px] rounded-xl bg-[#1C1C24] overflow-hidden flex items-center justify-center font-bold text-2xl shrink-0">
        {u.photo ? <img src={u.photo} alt="" className="h-full w-full object-cover" /> : (u.stage_name?.[0] || "?")}
      </button>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <button onClick={onProfile} className="font-head font-bold truncate hover:text-accent transition-colors">{u.stage_name}</button>
              {verified && <BadgeCheck className="h-4 w-4 text-accent shrink-0" aria-label="Verificado" />}
            </div>
            <div className="text-sm text-muted-foreground truncate">{primaryInstr}</div>
            {skillVerified && <div data-testid={`mf-verified-skill-${u.id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400"><BadgeCheck className="h-3.5 w-3.5" />Habilidade validada</div>}
            {cityStr && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" />{cityStr}</div>}
          </div>
          <button data-testid={`mf-fav-${u.id}`} onClick={onFav} aria-label={fav ? "Remover dos favoritos" : "Favoritar"} className="p-1 shrink-0">
            <Heart className={`h-5 w-5 transition-colors ${fav ? "text-rose-500 fill-rose-500" : "text-muted-foreground hover:text-rose-400"}`} />
          </button>
        </div>

        <div className="flex items-center gap-3 text-xs mt-1.5 flex-wrap">
          <span className="flex items-center gap-1 text-amber-400 font-semibold"><Star className="h-3.5 w-3.5 fill-amber-400" />{u.rating?.avg || "—"}{u.rating?.count ? <span className="text-muted-foreground font-normal">({u.rating.count})</span> : null}</span>
          {level && <span className="flex items-center gap-1 text-muted-foreground"><BarChart3 className="h-3.5 w-3.5" />{cap(level)}</span>}
          {u.availability_checked && <span data-testid={`mf-avail-${u.id}`} className={`flex items-center gap-1 font-medium ${u.available ? "text-emerald-400" : "text-rose-400"}`}><span className={`h-2 w-2 rounded-full ${u.available ? "bg-emerald-400" : "bg-rose-400"}`} />{u.available ? "Disponível" : "Indisponível"}</span>}
          {rate != null && <span className="text-accent font-mono font-semibold">a partir de {money(rate)}</span>}
        </div>

        <div className="flex items-center gap-2 mt-auto pt-2.5">
          {invited ? (
            <Button data-testid={`mf-invited-${u.id}`} disabled className="flex-1 h-9 rounded-xl font-semibold gap-1.5 bg-emerald-600/20 text-emerald-400 border border-emerald-600/30"><Check className="h-4 w-4" />Convidado</Button>
          ) : (
            <Button data-testid={`mf-invite-${u.id}`} onClick={onInvite} className="flex-1 h-9 bg-gradient-to-r from-[#FF4D00] to-accent text-white rounded-xl font-semibold gap-1.5"><Send className="h-4 w-4" />Convidar</Button>
          )}
          <Button data-testid={`mf-profile-${u.id}`} onClick={onProfile} variant="outline" className="h-9 rounded-xl bg-transparent border-white/10">Ver perfil</Button>
          <button data-testid={`mf-share-${u.id}`} onClick={onShare} aria-label="Compartilhar perfil" className="h-9 w-9 rounded-xl border border-white/10 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-white/20 shrink-0"><Share2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};
